import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const letterSpacingSettleSkill: CodeoSkill = {
    name: SKILL_NAMES.letterSpacingSettle,
    kind: 'codeo',
    description:
        'Draw a short line in from wide letter-spacing to its set tracking as it fades up — the ' +
        'quiet premium entrance for a title card, a brand line, or a closing statement.',
    documentUsesSkill: check((script) => /letterSpacing|letter-spacing/.test(script)),
    instructions: `# Letter-Spacing Settle

A short line arrives already in position, spaced too wide, and closes to its set tracking as it fades up. Nothing slides and nothing bounces; the only motion is the type tightening. It is the most restrained entrance in the library, and the right one when the words are supposed to carry the moment on their own.

Use it where a kinetic entrance would be too loud: a title card, a sign-off, a brand line, a solemn close.

## Markup

Center the line on the frame's optical center, and keep it short — this entrance has no way to make a long line interesting.

\`\`\`html
<div id="statement" style="position: absolute; left: 0; right: 0; top: 460px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 92px; font-weight: 300; color: var(--l5-color-0, #101820);">Built for the long run</div>
\`\`\`

## Timeline code

Tween \`letterSpacing\` itself. It is not a transform channel, so it composes freely with an opacity fade and never collides with a CSS \`transform\`.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#statement', { letterSpacing: '0.34em', opacity: 0 });

tl.to('#statement', {
    letterSpacing: '0.02em',
    opacity: 1,
    duration: 1.15,
    ease: 'power3.out',
}, 0.15);

window.__timelines = { main: tl };
\`\`\`

The long \`power3.out\` tail is the whole character of the move: the type rushes together and then eases the last few thousandths of an em, which is what makes it feel considered rather than mechanical.

## Adapt

- **Reach**: 0.34em → 0.02em is the standard. 0.6em is theatrical and wants a longer duration; 0.16em is barely perceptible and suits a subhead settling under a louder headline.
- **Direction**: start *tight* (\`-0.04em\`) and open to normal for a heavier, more pressurized arrival — better under a bold weight.
- **With a rise**: add \`y: 12\` to the from-state for a little lift. Any more than about 16px and it stops being this technique.
- **Exit**: reopening the tracking while fading out mirrors the entrance and closes a title card cleanly.
- **Under a headline**: run it on the second line, 0.25s after the headline lands — the delay reads as the line settling into the space the headline made.
- **Two weights**: pairing a light tracked line with a heavy headline is the one place a second font weight earns itself.

## Pitfalls

- \`letter-spacing\` adds trailing space after the final character, so a centered line drifts very slightly left as it tightens. Add \`padding-left\` equal to the final tracking, or accept it — at these values it is sub-pixel.
- Don't combine with a per-word or per-character stagger. The point is that the line moves as one object; splitting it makes this \`${SKILL_NAMES.kineticWordReveal}\` with extra steps.
- Keep the line under about six words. Wide tracking on a long line wraps unpredictably and the close then reflows the wrap.
- \`gsap.set\` establishes the from-state at build time, so the tween must not add \`immediateRender: false\`.
- This entrance leaves the frame completely still once it lands. It needs an ambient layer behind it — see \`${SKILL_NAMES.atmosphereGradientField}\` — or a slow push; see \`${SKILL_NAMES.cinematicCameraMove}\`.
- At heavy weights wide tracking looks like a mistake rather than a choice. Below about 500 weight is where this reads as premium.`,
};
